import { useEffect, useState } from "react";
import { Button, RichTextEditor } from "@legal-500/legal500-design-system";
import PermissionGate from "@/components/Permissions/PermissionGate";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { usePermissions } from "@/hooks/usePermissions";
import { marketingTerms, type TermsVersion } from "@/api/marketingTerms.service";
import styles from "./Terms.module.css";

type TermsEditorPanelProps = {
  /** The version staff may edit, or null before the terms have ever been published. */
  current: TermsVersion | null;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
  refetchTerms: () => void;
};

type ApiError = Error & { status?: number };

function saveErrorMessage(error: unknown): string {
  const apiError = error as ApiError;
  if (apiError?.status === 409) {
    return "Someone else saved these terms while they were open. Reopen them to see their text, then apply your change.";
  }
  if (apiError?.status === 404) {
    return "Nothing is published yet, so there is no text to correct. Publish the first version instead.";
  }
  return apiError?.message || "The terms could not be saved.";
}

function savedNote(current: TermsVersion): string {
  const published = `version ${current.version}, published ${new Date(
    current.publishedAt,
  ).toLocaleString()}${current.publishedBy ? ` by ${current.publishedBy}` : ""}`;

  if (current.updatedAt === current.publishedAt) {
    return `${published} · never corrected`;
  }

  return `${published} · last corrected ${new Date(current.updatedAt).toLocaleString()}${
    current.updatedBy ? ` by ${current.updatedBy}` : ""
  }`;
}

export function TermsEditorPanel({
  current,
  onSaved,
  onError,
  refetchTerms,
}: TermsEditorPanelProps) {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("staff.marketing.write");
  const [content, setContent] = useState(current?.content ?? "");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  useEffect(() => {
    setContent(current?.content ?? "");
  }, [current?.id, current?.revision]);

  async function handleCorrect() {
    if (!current) return;

    setSaving(true);
    try {
      await marketingTerms.correct({ content, expectedRevision: current.revision });
      onSaved("Correction saved. The version number is unchanged, so nobody accepts again.");
      refetchTerms();
    } catch (error) {
      onError(saveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    setPublishing(true);
    try {
      const result = await marketingTerms.publish(content);
      setConfirmingPublish(false);
      onSaved(
        `Version ${result.termsVersion.version} published. Every user at every firm must accept the terms again.`,
      );
      refetchTerms();
    } catch (error) {
      setConfirmingPublish(false);
      onError(saveErrorMessage(error));
    } finally {
      setPublishing(false);
    }
  }

  const nextVersion = (current?.version ?? 0) + 1;
  const publishLabel = current ? "Publish new version" : "Publish first version";

  return (
    <section className={styles.editor}>
      <header className={styles.editorHeader}>
        <div>
          <h2 className={styles.editorTitle}>Terms and conditions</h2>
          <p className={styles.savedNote}>
            {current ? savedNote(current) : "Not published yet"} · only the current version can be
            edited, earlier versions are history
          </p>
        </div>
      </header>

      <div className={styles.editorColumn}>
        <span className={styles.columnLabel}>Text</span>
        <RichTextEditor
          value={content}
          onChange={setContent}
          preset="basic_enhanced"
          minHeight={320}
          label=""
          disabled={!canWrite}
        />
      </div>

      <PermissionGate permission="staff.marketing.write">
        <div className={styles.actions}>
          {current && (
            <div className={styles.action}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCorrect}
                disabled={saving || publishing || content.length === 0}
              >
                {saving ? "Saving…" : "Save correction"}
              </Button>
              <p className={styles.actionNote}>
                Fixes a typo in the published text. Stays on version {current.version}, and nobody
                is asked to accept again.
              </p>
            </div>
          )}

          <div className={styles.action}>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setConfirmingPublish(true)}
              disabled={saving || publishing || content.length === 0}
            >
              {publishLabel}
            </Button>
            <p className={styles.actionNote}>
              {current
                ? `Creates version ${nextVersion}. Every user at every firm must accept the terms again before they can download.`
                : "Publishes this text as version 1. Firms accept it before they can download."}
            </p>
          </div>
        </div>
      </PermissionGate>

      {confirmingPublish && (
        <ConfirmationModal
          title={publishLabel}
          message={
            current
              ? `This publishes version ${nextVersion} of the Marketing Toolkit terms.`
              : "This publishes the Marketing Toolkit terms as version 1."
          }
          warningMessage={
            current
              ? "Every user at every firm must accept the terms again before they can download from the Marketing Toolkit. To fix a typo without asking anyone to accept again, close this and use Save correction."
              : "Every user at every firm must accept the terms before they can download from the Marketing Toolkit."
          }
          confirmButtonText={publishLabel}
          cancelButtonText="Cancel"
          onClose={() => setConfirmingPublish(false)}
          onConfirm={handlePublish}
        />
      )}
    </section>
  );
}
